import { format } from 'date-fns';

/**
 * Computes how many full calendar days remain before an archived project
 * is permanently deleted (30-day retention window).
 *
 * Uses UTC midnight normalization so the result is consistent regardless
 * of the viewer's local timezone.
 *
 * @param {string} archivedOnISO - ISO 8601 UTC datetime string (e.g. "2026-05-01T12:00:00")
 * @returns {number} days remaining (0 when the window has expired)
 */
export const computeDaysRemaining = (archivedOnISO) => {
  const archiveDate = new Date(archivedOnISO);
  const today = new Date();

  // Normalize both to start of UTC day
  const archiveDayMs = Date.UTC(
    archiveDate.getUTCFullYear(),
    archiveDate.getUTCMonth(),
    archiveDate.getUTCDate()
  );
  const todayDayMs = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  const daysDiff = Math.floor((todayDayMs - archiveDayMs) / (1000 * 60 * 60 * 24));
  return Math.max(0, 30 - daysDiff);
};

/**
 * Formats an archive date for display on an archived project card.
 *
 * Normalizes to UTC midnight before formatting so the displayed date matches
 * the UTC calendar date stored in the database, regardless of the viewer's
 * local timezone.
 *
 * @param {string} archivedOnISO - ISO 8601 UTC datetime string
 * @returns {string} e.g. "Archived Jun 2, 2026"
 */
export const formatArchiveDate = (archivedOnISO) => {
  const archiveDate = new Date(archivedOnISO);
  // Reconstruct as a local-time Date using UTC year/month/day so date-fns
  // format() renders the UTC calendar date rather than the shifted local date.
  const utcNormalized = new Date(
    archiveDate.getUTCFullYear(),
    archiveDate.getUTCMonth(),
    archiveDate.getUTCDate()
  );
  return `Archived ${format(utcNormalized, 'MMM d, yyyy')}`;
};

/**
 * Filters a project list by a case-insensitive substring match against
 * `title` and `created_by`. Applies identically to active and archived arrays.
 *
 * @param {string} query - Search string (empty string returns all projects)
 * @param {Array} projects - Array of project objects with `title` and `created_by` fields
 * @returns {Array} Filtered array (original array returned when query is empty/whitespace)
 */
export const filterProjects = (query, projects) => {
  if (!query || !query.trim()) return projects;
  const q = query.toLowerCase().trim();
  return projects.filter(
    (p) =>
      (p.title && p.title.toLowerCase().includes(q)) ||
      (p.created_by && p.created_by.toLowerCase().includes(q))
  );
};
